import "./polyfills";

// NOTE: We intentionally do NOT clear wagmi.store on page load.
// WagmiProvider uses reconnectOnMount so a refresh restores the last wallet
// session. Clearing localStorage here previously caused wagmi to lose its
// internal connection state during Vite HMR re-executions.
// AES key persistence is now handled exclusively by configureCotiPlugin's
// onboardingServices (coti-portal:aes-backup:* prefix). The legacy pod:aes-key:*
// entries no longer exist so no purge is needed.
if (typeof window !== "undefined") {
  Object.keys(localStorage)
    .filter(k => k.startsWith("pod-portal-requests:"))
    .forEach(k => { try { if (localStorage.getItem("pod-portal-storage-version") !== "v0.2") localStorage.removeItem(k); } catch { /* ignore */ } });
  try { localStorage.setItem("pod-portal-storage-version", "v0.2"); } catch { /* ignore */ }
}

import { createRoot } from "react-dom/client";
import { configureCotiPlugin, type EncryptedAesBackup } from "@coti-io/coti-wallet-plugin";
import { isMainnetPortalBuild } from "@/lib/networkMode";
import App from "./App.tsx";
import "./index.css";

const AES_BACKUP_STORAGE_PREFIX = "privacy-portal:aes-backup";

const aesBackupStorageKey = (chainId: number | string, address: string) =>
  `${AES_BACKUP_STORAGE_PREFIX}:${chainId}:${address}`;

// Bootstrap the plugin before React renders.
// This initialises encrypted backup services, onboarding grant config,
// and disconnect-key-clear behaviour. Must be called before createRoot.
configureCotiPlugin({
  // Fully disable Snap for this app (no install/probe/AES via Snap).
  snapEnabled: false,
  // Staging / mainnet builds onboard AES against COTI Mainnet even when the
  // wallet is temporarily on Ethereum or Avalanche.
  aesKeyChainId: isMainnetPortalBuild() ? 2632500 : undefined,
  debug: import.meta.env.DEV || import.meta.env.VITE_COTI_DEBUG === "true",
  walletConnectProjectId: import.meta.env.VITE_WALLETCONNECT_PROJECT_ID ?? "",
  sepoliaRpcUrl: import.meta.env.VITE_SEPOLIA_RPC_URL,
  cotiTestnetRpcUrl: import.meta.env.VITE_COTI_TESTNET_RPC_URL,
  onboardingGrantMinBalanceWei: import.meta.env.VITE_ONBOARDING_GRANT_MIN_BALANCE_COTI
    ? BigInt(Math.round(parseFloat(import.meta.env.VITE_ONBOARDING_GRANT_MIN_BALANCE_COTI) * 1e18))
    : undefined,
  // 0.4.5 ships built-in grant URLs + mainnet private/bridge addresses;
  // env overrides grant URLs when set.
  grantApiUrlTestnet: import.meta.env.VITE_GRANT_API_URL_TESTNET || undefined,
  grantApiUrlMainnet: import.meta.env.VITE_GRANT_API_URL_MAINNET || undefined,
  // Clear the in-memory session key when wagmi reports a disconnect so that
  // a subsequent wallet connection always starts from a clean AES state.
  clearSessionKeyOnWagmiDisconnect: true,
  onboardingServices: {
    mode: "custom",
    // Encrypted AES backup stored under the plugin-prescribed key format.
    fetchEncryptedAesBackup: async ({ chainId, address }) => {
      const normalized = address.toLowerCase();
      const key = aesBackupStorageKey(chainId, normalized);
      const raw =
        localStorage.getItem(key) ??
        localStorage.getItem(aesBackupStorageKey(chainId, address));
      if (!raw) return null;
      try {
        return JSON.parse(raw) as EncryptedAesBackup;
      } catch {
        localStorage.removeItem(key);
        return null;
      }
    },
    saveEncryptedAesBackup: async ({ chainId, address, backup }) => {
      const key = aesBackupStorageKey(chainId, address.toLowerCase());
      localStorage.setItem(key, JSON.stringify(backup));
    },
    replaceEncryptedAesBackup: async ({ chainId, address, backup }) => {
      const key = aesBackupStorageKey(chainId, address.toLowerCase());
      localStorage.setItem(key, JSON.stringify(backup));
    },
  },
});

createRoot(document.getElementById("root")!).render(<App />);
